import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaGraduationCap } from 'react-icons/fa';
import { saasApiAxios } from '../../lib/axios-interceptor';
import { getEnvConfig, isSaaS } from '../../constants';
import { getDashboardPath } from '../../utils/environment';

const SaaSLogin = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get environment configuration
      const envConfig = getEnvConfig();
      const tenant = envConfig.tenant;

      // Use the SaaS-specific axios instance which already has tenant headers configured
      const response = await saasApiAxios.post('/api/auth/login', formData);
      
      if (response.data.token && response.data.user) {
        // Store authentication data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('tenant', tenant);
        
        // Redirect to dashboard based on domain
        const isSaaSSubdomain = isSaaS();
        
        if (isSaaSSubdomain) {
          // Use the appropriate dashboard path based on environment
          window.location.href = getDashboardPath();
        } else {
          // For development or other cases, redirect to saas dashboard
          window.location.href = '/saas/dashboard';
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#DB4F40]/20 to-[#C64537]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-[#C64537]/20 to-[#B73E31]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#DB4F40]/10 to-[#C64537]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-sm w-full space-y-6 relative z-10">
        {/* Portal Header */}
        <div className="text-center">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-[#1B222A] mb-1">
              PhDScanner Portal
            </h1>
            <p className="text-[#1B222A]/70 text-xs">
              Academic Management System
            </p>
          </div>
        </div>

        {/* Logo and Header Section */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 mb-4 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300"></div>
            <div className="absolute inset-0 bg-white rounded-2xl shadow-lg flex items-center justify-center">
              <img 
                src="/phdscanner_logo.png" 
                alt="PhDScanner" 
                className="h-14 w-14 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <FaGraduationCap className="h-7 w-7 text-[#DB4F40] hidden" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-[#1B222A] mb-1">
            Welcome Back
          </h2>
          <p className="text-sm text-[#1B222A]/70 mb-6">
            Manage your PhD opportunities and academic positions
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 shadow-lg transform transition-all duration-300 animate-pulse">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-xs font-medium text-red-800">
                  Login Error
                </h3>
                <div className="mt-1 text-xs text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="bg-white/90 rounded-2xl shadow-2xl border border-white/20 p-6">
          <form className="space-y-4" onSubmit={handleLocalLogin}>
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-[#1B222A] mb-2">
                Username
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-4 w-4 text-gray-400 group-focus-within:text-[#DB4F40] transition-colors duration-200" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-[#1B222A] mb-2">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-[#DB4F40] transition-colors duration-200" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] hover:from-[#C64537] hover:to-[#B73E31] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DB4F40] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <span className="flex items-center">
                    Sign in
                    <svg className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-[#1B222A]/60">
            Secure access to your academic management portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default SaaSLogin;
